import React, { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfViewerProps {
    fileUrl: string;
    className?: string;
    showControls?: boolean;
}

const MAX_PAGE_WIDTH = 1100;
const MIN_PAGE_WIDTH = 280;
const PAGE_HORIZONTAL_PADDING = 24;
type ViewMode = "paged" | "scroll";

const PdfViewer: React.FC<PdfViewerProps> = ({
    fileUrl,
    className = "",
    showControls = true,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const pageContainersRef = useRef<Array<HTMLDivElement | null>>([]);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [numPages, setNumPages] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageInputValue, setPageInputValue] = useState<string>("1");
    const [zoom, setZoom] = useState<number>(1);
    const [viewMode, setViewMode] = useState<ViewMode>("scroll");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    useEffect(() => {
        setIsLoading(true);
        setLoadError(null);
        setNumPages(0);
        setCurrentPage(1);
        setPageInputValue("1");
        setZoom(1);
        pageContainersRef.current = [];
    }, [fileUrl]);

    useEffect(() => {
        const updateWidth = () => {
            const width = containerRef.current?.clientWidth ?? 0;
            setContainerWidth(width);
        };

        updateWidth();
        window.addEventListener("resize", updateWidth);

        return () => {
            window.removeEventListener("resize", updateWidth);
        };
    }, []);

    const pageWidth = Math.min(
        MAX_PAGE_WIDTH,
        Math.max(containerWidth - PAGE_HORIZONTAL_PADDING, MIN_PAGE_WIDTH)
    );
    const renderWidth = Math.floor(pageWidth * zoom);

    const documentFile = useMemo(
        () => ({ url: fileUrl, withCredentials: true }),
        [fileUrl]
    );

    const goToPage = (page: number) => {
        if (numPages <= 0) return;
        const safePage = Math.min(Math.max(page, 1), numPages);
        setCurrentPage(safePage);
        setPageInputValue(safePage.toString());

        if (viewMode === "scroll") {
            pageContainersRef.current[safePage - 1]?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    };

    const handlePageInputBlur = () => {
        const parsed = Number.parseInt(pageInputValue, 10);
        if (Number.isNaN(parsed)) {
            setPageInputValue(currentPage.toString());
            return;
        }
        goToPage(parsed);
    };

    const handleViewerScroll = () => {
        if (viewMode !== "scroll") return;
        const container = containerRef.current;
        if (!container || numPages <= 0) return;

        const scanLine = container.scrollTop + container.clientHeight * 0.35;
        let visiblePage = 1;

        for (let i = 0; i < numPages; i++) {
            const el = pageContainersRef.current[i];
            if (!el) continue;
            if (scanLine >= el.offsetTop) {
                visiblePage = i + 1;
            } else {
                break;
            }
        }

        if (visiblePage !== currentPage) {
            setCurrentPage(visiblePage);
            setPageInputValue(visiblePage.toString());
        }
    };

    if (!fileUrl) {
        return (
            <div className={`w-full h-full flex items-center justify-center text-gray-500 ${className}`}>
                No PDF file available.
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            onScroll={handleViewerScroll}
            className={`w-full h-full overflow-auto bg-slate-100 ${className}`}
        >
            {showControls && (
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200 p-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={isLoading || currentPage <= 1}
                            className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                        >
                            Prev
                        </button>
                        <button
                            type="button"
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={isLoading || numPages <= 0 || currentPage >= numPages}
                            className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                        >
                            Next
                        </button>

                        <div className="flex items-center gap-2 ml-2">
                            <span className="text-sm text-gray-700">Page</span>
                            <input
                                type="number"
                                min={1}
                                max={numPages || 1}
                                value={pageInputValue}
                                disabled={isLoading || numPages <= 0}
                                onChange={(e) => setPageInputValue(e.target.value)}
                                onBlur={handlePageInputBlur}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        handlePageInputBlur();
                                    }
                                }}
                                className="w-16 px-2 py-1 border border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">/ {numPages || "-"}</span>
                        </div>

                        <div className="w-px h-6 bg-gray-300 mx-2" />

                        <button
                            type="button"
                            onClick={() => setZoom((prev) => Math.max(prev - 0.2, 0.6))}
                            disabled={isLoading}
                            className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                        >
                            -
                        </button>
                        <span className="text-sm text-gray-700 min-w-14 text-center">
                            {Math.round(zoom * 100)}%
                        </span>
                        <button
                            type="button"
                            onClick={() => setZoom((prev) => Math.min(prev + 0.2, 2.5))}
                            disabled={isLoading}
                            className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                        >
                            +
                        </button>
                        <button
                            type="button"
                            onClick={() => setZoom(1)}
                            disabled={isLoading}
                            className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                        >
                            Fit
                        </button>

                        <div className="w-px h-6 bg-gray-300 mx-2" />

                        <div className="inline-flex rounded border border-gray-300 overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setViewMode("paged")}
                                className={`px-3 py-1 text-sm ${
                                    viewMode === "paged" ? "bg-blue-600 text-white" : "bg-white text-gray-700"
                                }`}
                            >
                                Paged
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setViewMode("scroll");
                                    requestAnimationFrame(() => {
                                        pageContainersRef.current[currentPage - 1]?.scrollIntoView({
                                            behavior: "smooth",
                                            block: "start",
                                        });
                                    });
                                }}
                                className={`px-3 py-1 text-sm ${
                                    viewMode === "scroll" ? "bg-blue-600 text-white" : "bg-white text-gray-700"
                                }`}
                            >
                                Scroll
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="w-full min-h-full flex justify-center">
                <Document
                    file={documentFile}
                    loading=""
                    onLoadSuccess={({ numPages: pages }) => {
                        setNumPages(pages);
                        setCurrentPage(1);
                        setPageInputValue("1");
                        setIsLoading(false);
                    }}
                    onLoadError={(err) => {
                        setLoadError(`Unable to load PDF: ${err.message}`);
                        setIsLoading(false);
                    }}
                    onSourceError={(err) => {
                        setLoadError(`Unable to load PDF source: ${err.message}`);
                        setIsLoading(false);
                    }}
                    className={`w-full flex flex-col items-center ${viewMode === "scroll" ? "gap-6 py-6" : "gap-3 py-3"}`}
                >
                    {numPages > 0 && viewMode === "scroll" && (
                        <>
                            {Array.from({ length: numPages }, (_, index) => (
                                <div
                                    key={`pdf-page-wrap-${index + 1}`}
                                    ref={(el) => {
                                        pageContainersRef.current[index] = el;
                                    }}
                                    className="w-full flex flex-col items-center"
                                >
                                    {index > 0 && (
                                        <div className="w-full flex items-center justify-center gap-3 mb-3">
                                            <span className="h-px w-16 bg-gray-300" />
                                            <span className="text-xs text-gray-500 uppercase tracking-wide">
                                                Page {index + 1}
                                            </span>
                                            <span className="h-px w-16 bg-gray-300" />
                                        </div>
                                    )}
                                    <div className="bg-white shadow-md ring-1 ring-gray-300">
                                        <Page
                                            key={`pdf-page-${index + 1}`}
                                            pageNumber={index + 1}
                                            width={renderWidth}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                        />
                                    </div>
                                </div>
                            ))}
                        </>
                    )}

                    {numPages > 0 && viewMode === "paged" && (
                        <div
                            ref={(el) => {
                                pageContainersRef.current[currentPage - 1] = el;
                            }}
                            className="bg-white shadow-md ring-1 ring-gray-300"
                        >
                            <Page
                                key={`pdf-page-${currentPage}`}
                                pageNumber={currentPage}
                                width={renderWidth}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                            />
                        </div>
                    )}
                </Document>
            </div>

            {isLoading && (
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                    Loading PDF...
                </div>
            )}

            {loadError && (
                <div className="w-full p-4 text-center text-red-500">
                    {loadError}
                </div>
            )}
        </div>
    );
};
export default PdfViewer;
